import { IdentifierType, ListingCategories } from '@ddt-web/common';
import { ApiParamPositionImpl, SharedApplicationConstants, WebUserDataConstants } from '@ddt-web/domain';
import { PortfolioIdUtil, PortfolioParamBuilder } from '@ddt-web/portfolio-data';
import * as get from 'lodash/get';
import * as isEmpty from 'lodash/isEmpty';

import { AnalysisParams } from '../analysis-params';
import { BenchmarkConfig } from '../config/benchmark.config';
import { DataServiceApiConstants } from '../constants/data-service-api.constants';
import { ApiStoreConfiguration } from '../models/api-store-configuration.interface';
import { PortfolioAnalysisServiceParams } from '../models/portfolio-analysis-service-params.interface';

export class BenchmarkUtil {
	/**
	 * @description getPortfoliosParamsConfig: constructs the portfolios property for the PortfolioAnalysisServiceParams object.
	 * @param {any} portfolios: supports both a non-typed response object/and a strongly typed object. I think this should/can be refactored at
	 * some point in the future. For now I will leave it as is as I don't want to break the data-portfolio-analysis service layer.
	 */
	// TODO agrigore: remove as part of cleanup
	public static getPortfoliosParamsConfig(portfolios: Array<any>): Array<any> {
		return portfolios.map(portfolio => {
			if (portfolio && portfolio.portfolioId) {
				return PortfolioParamBuilder.construct().setIdentifier(portfolio.portfolioId).build();
			} else if (portfolio && (portfolio.uniqueId || portfolio.id)) {
				const dataObj = {
					id: portfolio.uniqueId || portfolio.id
				} as any;
				if (portfolio.uniqueId) {
					dataObj.type = ListingCategories.USER_PORTFOLIO;
				}

				const holdings = portfolio.positions || portfolio.holdings;
				if (holdings) {
					dataObj.positions = holdings
						.filter(pos => {
							return !pos.positionType || pos.positionType !== 'INVALID';
						})
						.map(pos => {
							return {
								identifier: pos.identifier || pos.securityId,
								identifierType: pos.identifierType || 'SECURITY_ID',
								weight: pos.weight
							};
						});
				}
				return dataObj;
			}
		});
	}

	/**
	 * @description getDefaultBenchmark: get the benchmark for default request types: uses S&P 500 benchmark.
	 * @param {ApiStoreConfiguration} config: contains store configuration properties.
	 * @param {AnalysisParams} analysisParams: the params used to build the api call.
	 * @param {enum} asOfDateType: describes the state of the date type: LATEST, APPROVED, and PRE_APPROVED.
	 */
	public static getDefaultBenchmark(
		config: ApiStoreConfiguration,
		analysisParams: AnalysisParams,
		asOfDateType: string = DataServiceApiConstants.AS_OF_DATE_TYPES.APPROVED
	): PortfolioAnalysisServiceParams {
		const benchmarkArray = [];
		const riskBenchmarkArray = [];
		const result: PortfolioAnalysisServiceParams = {
			asOfDateType: asOfDateType
		};
		const portfoliosConfig = this.getPortfoliosParamsConfig(analysisParams.analysisParamsArray);
		const performanceStores = [
			DataServiceApiConstants.STORES.PERFORMANCE,
			DataServiceApiConstants.STORES.NET_PERFORMANCE,
			DataServiceApiConstants.STORES.NET_CUSTOM_PERFORMANCE
		];
		const performanceCommonPeriodsStores = [
			DataServiceApiConstants.STORES.PERFORMANCE_COMMON_PERIODS,
			DataServiceApiConstants.STORES.NET_PERFORMANCE_COMMON_PERIODS,
			DataServiceApiConstants.STORES.NET_CUSTOM_PERFORMANCE_COMMON_PERIODS
		];
		const isRiskNPerformanceConfig = [
			DataServiceApiConstants.STORES.RISK,
			...performanceStores,
			...performanceCommonPeriodsStores
		].includes(config.name);

		result['portfolios'] = JSON.stringify(portfoliosConfig);

		// supported types to identiy model (model - via engagement response, MODEL_PORTFOLIO - via AC Server response)
		const supportedModelTypes = [SharedApplicationConstants.PORTFOLIO_TYPE_MODEL, WebUserDataConstants.SUBTYPE.MODEL_PORTFOLIO];
		if (
			supportedModelTypes.includes(analysisParams?.benchmark?.type) &&
			PortfolioIdUtil.isBlackRockModel(analysisParams?.benchmark?.id)
		) {
			// check here for engagement with model benchmark
			benchmarkArray.push({
				positions: [new ApiParamPositionImpl(
					analysisParams?.benchmark?.id,
					IdentifierType.PORTFOLIO_ID,
					100
				)],
				type: ListingCategories.MODEL_PORTFOLIO
			});
		} else if (!isEmpty(get(analysisParams, 'benchmark.positions'))) {
			benchmarkArray.push({
				positions: get(analysisParams, 'benchmark.positions'),
				type: get(analysisParams, 'benchmark.type')
			});
		}

		if (!isEmpty(get(analysisParams, 'riskBenchmark.positions'))) {
			riskBenchmarkArray.push({
				positions: get(analysisParams, 'riskBenchmark.positions'),
				type: get(analysisParams, 'riskBenchmark.type')
			});
		}

		if (isRiskNPerformanceConfig) {
			if (!isEmpty(benchmarkArray)) {
				const benchmarkJSON = JSON.parse(JSON.stringify(benchmarkArray));
				const riskBenchmarkJson = isEmpty(riskBenchmarkArray) ? JSON.parse(JSON.stringify(benchmarkArray)) : JSON.parse(JSON.stringify(riskBenchmarkArray));
				benchmarkJSON.map((res, idx) => (res.id = DataServiceApiConstants.BENCHMARK_TYPES.DEFAULT + idx));
				riskBenchmarkJson.map((res, idx) => (res.id = DataServiceApiConstants.BENCHMARK_TYPES.DEFAULT + idx));
				const beta = BenchmarkConfig.betaPortfolios.concat(benchmarkJSON);
				Object.assign(result, { betaPortfolios: JSON.stringify(beta), riskBenchmarkPortfolios: JSON.stringify(riskBenchmarkJson) });
			} else {
				Object.assign(result, { betaPortfolios: JSON.stringify(BenchmarkConfig.betaPortfolios) });
			}
		}

		if (performanceCommonPeriodsStores.includes(config.name)) {
			result.asOfDateType = 'LATEST';
		} else if (config.asOfDateType) {
			result.asOfDateType = config.asOfDateType;
		}

		return result;
	}
}
